// Live's Generate Audio in a template's form (spec G14-templates PR 13, decision 2: an approval
// is a job made before the run): the voice reading the script, a speech of the brand's
// (`audio.tts` on ElevenLabs v3: Talking Head Overlay's `DEFAULT_TEXT_TO_SPEECH_MODEL_ID`; Your
// Personal Clone's older call names no model and live's backend runs it on `eleven_v3` too),
// which the run then takes as its voiceover. Live's button (`GenerationStatusButton`, never
// "just generated" in these forms), its failure mark, and its tile (`VoiceAudioTile`): the voice
// to pick, a loader while the speech is made, then the speech's player whose x removes it.
import { Box, Button, Group, Loader, Text, Tooltip, UnstyledButton, VisuallyHidden } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { AudioLines, CircleAlert, RefreshCcw, X } from "lucide-react";
import { useState } from "react";

import { quoteJob, startJob, useGetJob } from "../../../api/generated/endpoints/generation/generation";
import type { Job, OutputRef } from "../../../api/generated/model";
import { useIdempotencyKey } from "../../../platform/idempotency";
import { jobRefetchInterval } from "../../../platform/jobs/refetch";
import { useStreamStatus } from "../../../platform/stream-status";
import { AudioPlayer } from "../../../ui/AudioPlayer";
import classes from "./forms.module.css";

/** Live's voiceover model (Talking Head Overlay's `DEFAULT_TEXT_TO_SPEECH_MODEL_ID`). */
export const VOICEOVER_MODEL = "elevenlabs-v3";
/** Live's words for the button's states (`GENERATE_AUDIO_LABELS`). */
const WORDS = {
  generate: "Generate Audio",
  generating: "Generating Audio",
  regenerate: "Re-generate Audio",
} as const;
export const NEEDS_VOICE = "Select a voice";
export const NEEDS_SCRIPT = "Write or generate a script";
/** Live's words when a speech fails without the backend's own (`useCloneGenerationFlow`). */
export const AUDIO_FAILED = "Audio generation failed. Try again.";
/** Ours, where live says nothing: Voice Settings stays open while a voiceover is made and its
 * picks wait for it, so the panel says why a voice picked or made there does not take. */
export const VOICE_WAITS = "Voice changes wait until the audio is ready";

export interface Voiceover {
  /** The speech, once made: the run's voiceover. */
  ref: OutputRef;
  url: string;
  durationMs: number | null;
}

/** A speech job's file, once it succeeded with one. */
function voiceoverOf(job: Job | undefined): Voiceover | null {
  const output = job?.status === "succeeded" ? job.outputs[0] : undefined;
  if (job === undefined || output?.url == null) return null;
  return {
    ref: { kind: "job_output", job_id: job.id, output_index: output.index },
    url: output.url,
    durationMs: output.duration_ms,
  };
}

/**
 * The voiceover of a voice and a script: started on Generate Audio and read as a job's page
 * reads one. The last speech made stays while a new one is made, and after one that failed, as
 * live's; it is forgotten (`clear`) when the script or the voice changes, as live's
 * `useInvalidateAudio`, or by its tile's x. The forms lock while one is made, so nothing
 * forgets a speech still being paid for.
 */
export function useVoiceover(teamId: string, brandId: string) {
  const stream = useStreamStatus();
  const idempotency = useIdempotencyKey();
  // The latest speech asked for, and the one made before it (what stays while it is made).
  const [current, setCurrent] = useState<string | null>(null);
  const [previous, setPrevious] = useState<string | null>(null);
  const start = useMutation({
    mutationFn: async (asked: { script: string; voiceId: string }) => {
      const data = {
        type: "audio.tts" as const,
        model: VOICEOVER_MODEL,
        prompt: asked.script.trim(),
        voice_id: asked.voiceId,
      };
      const quote = await quoteJob(teamId, brandId, data);
      const body = { ...data, expected_quote_max: quote.credits_max };
      const key = idempotency.keyFor(body);
      try {
        return await startJob(teamId, brandId, body, { "Idempotency-Key": key });
      } catch (error) {
        idempotency.failed(key, body, error);
        throw error;
      }
    },
  });
  const currentJob = useGetJob(teamId, current ?? "", {
    query: {
      enabled: current !== null,
      refetchInterval: (query) => jobRefetchInterval(query.state.data?.status, stream),
    },
  }).data;
  const previousJob = useGetJob(teamId, previous ?? "", { query: { enabled: previous !== null } }).data;
  const currentMade = voiceoverOf(currentJob);
  const previousMade = voiceoverOf(previousJob);
  const made = currentMade ?? previousMade;
  const madeId = currentMade !== null ? current : previousMade !== null ? previous : null;
  const running =
    start.isPending ||
    (current !== null && (currentJob === undefined || currentJob.status === "queued" || currentJob.status === "running"));
  const ended = currentJob !== undefined && !running && currentMade === null;
  return {
    start: (asked: { script: string; voiceId: string }) =>
      start.mutate(asked, {
        onSuccess: (job) => {
          setPrevious(madeId);
          setCurrent(job.id);
        },
      }),
    running,
    /** A start the API refused (the quote, the balance). */
    error: start.isError ? start.error : null,
    /** The last speech's failure, in the backend's words or live's. */
    failure: ended ? (currentJob.error?.message ?? AUDIO_FAILED) : null,
    made,
    clear: () => {
      setCurrent(null);
      setPrevious(null);
      start.reset();
    },
  };
}

export type VoiceoverState = ReturnType<typeof useVoiceover>;

/**
 * Live's Generate Audio (`GenerationStatusButton`): "Generating Audio" with a loader while the
 * speech is made, "Re-generate Audio" in outline once one exists, else "Generate Audio"; what is
 * missing on hover; and live's red mark with the last failure's words beside it.
 */
export function VoiceoverButton(props: {
  voiceover: VoiceoverState;
  script: string;
  voiceId: string | null;
  disabled: boolean;
}) {
  const { voiceover, script, voiceId, disabled } = props;
  const blockers = [...(voiceId === null ? [NEEDS_VOICE] : []), ...(script.trim() === "" ? [NEEDS_SCRIPT] : [])];
  const again = voiceover.made !== null;
  const label = voiceover.running ? WORDS.generating : again ? WORDS.regenerate : WORDS.generate;
  const icon = voiceover.running ? <Loader size={16} /> : again ? <RefreshCcw size={16} aria-hidden /> : null;
  return (
    <Group gap={8} wrap="nowrap">
      <Tooltip
        label={blockers.map((line) => (
          <Box key={line}>{line}</Box>
        ))}
        disabled={blockers.length === 0}
        withArrow
      >
        <span>
          <Button
            variant={again ? "outline" : "filled"}
            leftSection={icon}
            disabled={disabled || blockers.length > 0 || voiceover.running}
            onClick={() => voiceId !== null && voiceover.start({ script, voiceId })}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
      {voiceover.failure === null || voiceover.running ? null : (
        <Tooltip label={voiceover.failure} multiline maw={350} withArrow>
          <span className={classes.failed}>
            <CircleAlert size={20} aria-hidden />
            <VisuallyHidden>{voiceover.failure}</VisuallyHidden>
          </span>
        </Tooltip>
      )}
    </Group>
  );
}

export interface VoiceAudioTileProps {
  /** The voice chosen, or none: the tile's label ("Voice Settings" without one). */
  voiceName: string | null;
  /** The speech made with the voice; none where the tile only names the voice (Claymation Ad's
   * narrator, whose takes come after the script). */
  voiceover?: VoiceoverState;
  /** The form's tooltip on the tile, where it has one. */
  tip: string | null;
  /** Greyed and closed (the form submitting; Talking Head Overlay's base video's own sound). */
  disabled: boolean;
  onOpen: () => void;
}

/**
 * Live's voice tile of these forms (`VoiceAudioTile`, the lip-sync's port): the voice to pick
 * (its name, the form's tooltip, no x), a loader while the speech is made, then the speech's
 * player, named "Audio", whose x removes the speech and keeps the voice.
 */
export function VoiceAudioTile({ voiceName, voiceover, tip, disabled, onOpen }: VoiceAudioTileProps) {
  const label = voiceName ?? "Voice Settings";
  if (voiceover?.running === true) {
    return (
      <span className={classes.voiceTile} aria-busy>
        <Loader size={24} aria-label="Making the audio" />
      </span>
    );
  }
  const made = voiceover?.made ?? null;
  if (voiceover !== undefined && made !== null) {
    return (
      <span className={classes.voiceTile} data-filled>
        <AudioPlayer
          url={made.url}
          name="Audio"
          durationSeconds={made.durationMs === null ? null : made.durationMs / 1000}
          seek={false}
        />
        <UnstyledButton
          className={classes.voiceRemove}
          aria-label="Remove the audio"
          disabled={disabled}
          onClick={voiceover.clear}
        >
          <X size={14} aria-hidden />
        </UnstyledButton>
      </span>
    );
  }
  return (
    <Tooltip label={tip} disabled={tip === null} multiline maw={280} withArrow>
      <UnstyledButton
        className={classes.voiceTile}
        aria-label={voiceName === null ? "Voice Settings" : `Voice Settings: ${voiceName}`}
        disabled={disabled}
        onClick={onOpen}
      >
        <AudioLines size={20} strokeWidth={1.5} aria-hidden />
        <span className={classes.voiceTileName}>{label}</span>
      </UnstyledButton>
    </Tooltip>
  );
}

/** The line Voice Settings shows at its top (its `head`) while a voiceover is made. */
export function VoiceWaits({ running }: { running: boolean }) {
  return running ? <Text className={classes.voiceWaits}>{VOICE_WAITS}</Text> : null;
}
